<template>
  <fieldset v-if="subCategories && subCategories.length" tabindex="0" :aria-label="$awt('aw.common.components.product_list.btn_filter.label')">
    <legend class="awSrOnly">
      {{ $awt('aw.common.components.product_list.btn_filter.label') }}
    </legend>
    <aw-show-more-version2
      v-if="view === filterViews.FULL"
      :close-text="$awt('aw.common.components.product_list.btn_filter.show_more.close')"
      :open-text="$awt('aw.common.components.product_list.btn_filter.show_more.open')"
      :max-content-height="225"
      :class="$style.showMore"
    >
      <aw-radio-group
        v-model="currentSelectedRadio"
        column-space="sm"
      >
        <aw-radio-version2
          v-for="(category, i) in subCategories"
          :key="i"
          :label="category.name"
          :value="category.value"
        />
      </aw-radio-group>
    </aw-show-more-version2>
    <aw-select-version2
      v-else
      v-model:selected-options="currentSelectedSelect"
      :options="subCategories"
      :label="$awt('aw.common.components.product_list.btn_filter.all_products')"
      :inline-label-text="$awt('aw.common.components.product_list.btn_filter.inline_label_text')"
      :multiple="false"
      :class="$style.subcategorySelect"
      :dropdown-icon-attrs="{
        name: 'arrow-down-16',
        size: 20
      }"
    />
  </fieldset>
</template>

<script>
  import { mapState } from 'pinia';
  import { defineAsyncComponent, nextTick } from 'vue';
  import { extractResourceFromPath } from '~~/common/utils/urlResource.js';
  import { PUSH_FILTER_PRODUCT } from '~~/common/plugins/aw-analytics.js';
  import { useCategoryStore } from '~~/common/stores/category';
  import { useProductsStore } from '~~/common/stores/products';
  import AwRadioGroup from '~~/common/components/Common/Widgets/AwRadioGroup.vue';
  import AwRadioVersion2 from '~~/common/components/Common/Widgets/AwRadioVersion2.vue';
  import AwShowMoreVersion2 from '~~/common/components/Common/AwShowMoreVersion2.vue';

  const filterViews = {
    COMPACT: 'COMPACT',
    FULL: 'FULL',
  };

  export default {
    name: 'AwSubCategoryFilter',
    components: {
      AwSelectVersion2: defineAsyncComponent(() => import('~~/common/components/Common/AwSelectVersion2')),
      AwShowMoreVersion2,
      AwRadioVersion2,
      AwRadioGroup,
    },
    props: {
      isListLoading: {
        type: Boolean,
        default: false,
      },
      view: {
        type: String,
        required: true,
        validator: view => view in filterViews,
      },
    },
    emits: ['activate-filter'],
    data () {
      return {
        activeCategories: [],
        isLoading: false,
        filterViews,
        defaultValue: {
          name: this.$awt('aw.common.components.product_list.btn_filter.all_products'),
          value: 0,
        },
      };
    },
    computed: {
      ...mapState(useProductsStore, {
        products: state => state,
      }),
      subCategories () {
        const subCategoryFilter = this.products.filters.find(filter => filter.id === 'subcategory');
        if (!subCategoryFilter) {
          return [];
        }
        const subCats = subCategoryFilter.values;
        return [this.defaultValue, ...subCats];
      },
      filterBy () {
        return this.products.filterBy;
      },
      currentSelectedRadio: {
        get () {
          return this.subCategories.find(category => this.isEnabled(category.value))?.value || this.defaultValue.value;
        },
        set: async function (value) {
          if (value) {
            await this.activateFilter({ subcategory: value }, !this.isEnabled(value));
          } else {
            this.resetCategories();
          }
        },
      },
      currentSelectedSelect: {
        get () {
          return this.subCategories.find(category => this.isEnabled(category.value)) || this.defaultValue;
        },
        set: async function ({ value }) {
          if (value) {
            await this.activateFilter({ subcategory: value }, !this.isEnabled(value));
          } else {
            this.resetCategories();
          }
        },
      },
    },
    watch: {
      isListLoading (newVal) {
        this.isLoading = newVal;
      },
    },
    methods: {
      isEnabled (value) {
        return this.filterBy?.subcategory?.length && this.filterBy.subcategory.includes(value);
      },
      activateFilter (value, isActivation) {
        this.isLoading = true;
        const catgeoryStore = useCategoryStore();
        const productsStore = useProductsStore();

        productsStore.resetFilterBy();
        nextTick(() => {
          if (isActivation) {
            productsStore.setActiveFilter(value);
            try {
              const urlResource = extractResourceFromPath(this.$getHref());
              Object.values(value).forEach((v) => {
                this.$awAnalytics[PUSH_FILTER_PRODUCT]({
                  key: catgeoryStore.getCategoryFromTreeById({ treeId: parseInt(urlResource?.resourceId) })?.name,
                  value: this.subCategories?.find?.(fv => fv.value === v)?.name,
                });
              });
            } catch (err) {
              this.$logger.error(err);
            }
          }
          this.$emit('activate-filter');
        });
      },
      resetCategories () {
        this.isLoading = true;
        const productsStore = useProductsStore();
        productsStore.resetFilterByCategory();
        this.$emit('activate-filter');
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.subcategorySelect {
  position: relative;
  z-index: 10001;
}

.spinner {
  color: $color-text-primary;
}
</style>
